import React, {
  useCallback, useContext, useEffect, useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TileGroup } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useDeliveryOptionsContext } from '../../context/DeliveryOptionsContext';
import { DeliveryRefinement } from '../Refinements/DeliveryRefinement';
import { FulfillmentRefinement } from '../Refinements/FulfillmentRefinement';
import {
  BROWSE_ALL_REFINEMENT,
  FULFILLMENT_DIMENSIONS_LABELS,
  FULFILLMENT_LABELS,
  DELIVERY_REFINEMENTS,
  DELIVERY_REFINEMENTS_LABELS
} from '../../utils/constants';
import {
  getFulfillmentSubheaderLabel,
  findMatchingRefinementInDimension
} from '../../utils/fulfillment-helpers';
import { sortRefinements } from '../../product-results-helpers';

const wrapperClassNames = classNames(
  'sui-max-h-[275px]',
  'sui-overflow-y-auto'
);

const containerClassNames = classNames(
  'sui-flex',
  'sui-flex-wrap',
  'sui-flex-row',
  'sui-gap-3',
  'sui-mb-1',
  'multi-select-refinement-secondary-drawer'
);

const FulfillmentDimension = ({
  dimension,
  onChange,
  parentID,
  storeName
}) => {
  const { deliveryZip, isConsumerApp } = useContext(ExperienceContext);
  const {
    setTriggerDeliverySpeedChange,
    selectedFulfillmentDimensionLabel,
    appliedDimensionLabel,
    appliedRefinementKey,
    forceInitialSync,
    setForceInitialSync
  } = useDeliveryOptionsContext();
  const [selectedRefinementKey, setSelectedRefinementKey] = useState(
    appliedRefinementKey || BROWSE_ALL_REFINEMENT.refinementKey
  );
  const [selectedTile, setSelectedTile] = useState(selectedFulfillmentDimensionLabel ?? BROWSE_ALL_REFINEMENT.label);

  const { refinements = [] } = dimension || {};

  const handleTileClick = useCallback(({ refinement, dimension: clickedDimension, subHeaderLabel }) => {
    setSelectedTile(
      DELIVERY_REFINEMENTS_LABELS.includes(refinement?.label) ? FULFILLMENT_LABELS.DELIVERY : refinement?.label);
    setSelectedRefinementKey(refinement?.refinementKey);

    if (refinement?.label?.includes(FULFILLMENT_LABELS.DELIVERY)) {
      setTriggerDeliverySpeedChange(DELIVERY_REFINEMENTS.ANYTIME);
    }

    if (typeof onChange === 'function') {
      if (refinement?.label?.includes(FULFILLMENT_LABELS.DELIVERY) && refinement?.refinements) {
        const deliveryRefinement = refinement.refinements.find((item) => item.label === DELIVERY_REFINEMENTS.ANYTIME);

        if (!deliveryRefinement) {
          return;
        }

        onChange({
          refinement: deliveryRefinement,
          dimension: refinement,
          subHeaderLabel
        });
      } else {
        onChange({ refinement, dimension: clickedDimension, subHeaderLabel });
      }
    }
  }, [onChange]);

  const triggerTileChange = useCallback((updatedDimensionLabel, updatedRefinementKey) => {
    // Ensure the custom fulfillment dimension has more than one refinement
    if (dimension?.refinements?.length <= 1) {
      return;
    }

    const match = findMatchingRefinementInDimension(
      dimension, updatedRefinementKey
    );

    if (!match) {
      return;
    }

    const { refinement: matchedRefinement, dimension: matchedDimension } = match;

    const subHeaderLabel = getFulfillmentSubheaderLabel(
      updatedDimensionLabel, storeName, isConsumerApp, deliveryZip
    );
    const dimensionData = matchedDimension || { label: updatedDimensionLabel };

    handleTileClick({
      refinement: matchedRefinement,
      dimension: dimensionData,
      subHeaderLabel
    });
  }, [isConsumerApp, dimension, deliveryZip, storeName]);

  const handleDeliveryDimensionChange = useCallback(({ refinement, dimension: clickedDimension }) => {
    if (!/delivery/i.test(selectedTile)) {
      setSelectedTile(FULFILLMENT_LABELS.DELIVERY);
      setSelectedRefinementKey(refinement?.refinementKey);
    }

    const subHeaderLabel = getFulfillmentSubheaderLabel(
      FULFILLMENT_LABELS.DELIVERY, storeName, isConsumerApp, deliveryZip
    );

    setTriggerDeliverySpeedChange(refinement.label);
    if (typeof onChange === 'function') {
      const dimensionData = {
        ...clickedDimension,
        label: FULFILLMENT_DIMENSIONS_LABELS[clickedDimension.label] || clickedDimension.label
      };
      onChange({
        refinement,
        dimension: dimensionData,
        subHeaderLabel
      });
    }
  }, [deliveryZip, isConsumerApp, onChange, selectedTile]);

  const handleDeliverySpeedChange = useCallback(({ refinement, dimension: clickedDimension }) => {
    if (!/delivery/i.test(selectedTile)) {
      setSelectedTile(FULFILLMENT_LABELS.DELIVERY);
      setSelectedRefinementKey(refinement?.refinementKey);
    }

    const subHeaderLabel = getFulfillmentSubheaderLabel(
      FULFILLMENT_LABELS.DELIVERY, storeName, isConsumerApp, deliveryZip
    );

    if (typeof onChange === 'function') {
      onChange({ refinement, dimension: clickedDimension, subHeaderLabel });
    }
  }, [deliveryZip, isConsumerApp, onChange, selectedTile, storeName]);

  const renderRefinements = (refinementList) => (
    <TileGroup
      value={selectedTile}
      aria-label="Fulfillment Options"
      orientation="vertical"
      onChange={handleTileClick}
      grow
      key={selectedRefinementKey}
    >
      { refinementList
        .filter((refinement) => refinement.label?.toUpperCase() !== FULFILLMENT_LABELS.FREE_DELIVERY.toUpperCase())
        .sort(sortRefinements)
        .map((refinement, refinementIndex) => {
          const id = `${refinement.refinementKey}-${refinementIndex}`;
          const isRefinementSelected = /delivery/i.test(selectedTile)
            ? /delivery/i.test(refinement.label) : refinement.label === selectedTile;

          return (
            <FulfillmentRefinement
              key={id}
              dimension={dimension}
              id={id}
              refinement={refinement}
              isRefinementSelected={isRefinementSelected}
              parentID={parentID}
              selected={isRefinementSelected}
              storeName={storeName}
              isConsumerApp={isConsumerApp}
              deliveryZip={deliveryZip}
            />
          );
        })}
    </TileGroup>
  );

  useEffect(() => {
    if (appliedDimensionLabel && appliedRefinementKey) {
      triggerTileChange(appliedDimensionLabel, appliedRefinementKey);
    }
  }, [appliedDimensionLabel, appliedRefinementKey]);

  useEffect(() => {
    if (forceInitialSync) {
      triggerTileChange(appliedDimensionLabel, appliedRefinementKey);
      setForceInitialSync(false);
    }
  }, [
    appliedDimensionLabel,
    appliedRefinementKey,
    forceInitialSync,
    setForceInitialSync
  ]);

  return (
    <section className={wrapperClassNames}>
      <div className={containerClassNames} data-testid="instore-delivery-refinement-secondary-drawer">
        {refinements && renderRefinements(refinements)}
        <div className="sui-px-3 sui-overflow-hidden">
          <DeliveryRefinement
            onDimensionChange={handleDeliveryDimensionChange}
            onRefinementChange={handleDeliverySpeedChange}
            deselect={!/delivery/i.test(selectedTile)}
          />
        </div>
      </div>
    </section>
  );
};

FulfillmentDimension.propTypes = {
  dimension: PropTypes.shape({
    dimensionId: PropTypes.string,
    label: PropTypes.string,
    refinements: PropTypes.arrayOf(
      PropTypes.shape({})
    )
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  parentID: PropTypes.string,
  storeName: PropTypes.string
};

FulfillmentDimension.defaultProps = {
  parentID: '',
  storeName: ''
};

export { FulfillmentDimension };